<template>
  <div class="tree-container">
    <!-- Controles -->
    <div v-if="flatNodes.length > 0" class="controles">
      <button class="btn-controle" @click="zoomIn" title="Ampliar">
        <i class="material-icons">add</i>
      </button>
      <button class="btn-controle" @click="zoomOut" title="Reduzir">
        <i class="material-icons">remove</i>
      </button>
      <button class="btn-controle" @click="resetarView" title="Centralizar">
        <i class="material-icons">center_focus_strong</i>
      </button>
      <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
    </div>

    <svg
      v-if="flatNodes.length > 0"
      ref="svgEl"
      class="arvore-svg"
      :class="{ 'cursor-grabbing': arrastando }"
      @wheel.prevent="aoRodarMouse"
      @mousedown="iniciarArrasto"
      @mousemove="moverArrasto"
      @mouseup="finalizarArrasto"
      @mouseleave="finalizarArrasto"
    >
      <g :transform="`translate(${tx}, ${ty}) scale(${scale})`">
        <g class="edges">
          <line
            v-for="(edge, i) in edges"
            :key="'e' + i"
            :x1="edge.x1" :y1="edge.y1" :x2="edge.x2" :y2="edge.y2"
            stroke="#c7d2fe"
            stroke-width="2"
          />
        </g>
        <g class="nodes">
          <g
            v-for="no in flatNodes"
            :key="no.membroId"
            :transform="`translate(${no.x}, ${no.y})`"
            class="tree-node"
            @click.stop="clicarNo($event, no.membroId)"
            @mousedown.stop
          >
            <circle
              :r="nodeRadius"
              :fill="no.isCentral ? '#4f46e5' : '#ffffff'"
              :stroke="no.isCentral ? '#fb6340' : '#4f46e5'"
              stroke-width="2.5"
            />
            <text text-anchor="middle" :y="4"
                  :fill="no.isCentral ? '#ffffff' : '#2D3A7C'"
                  font-size="11" font-weight="700"
                  style="pointer-events: none; user-select: none;">
              {{ iniciais(no.nome) }}
            </text>
            <text text-anchor="middle" :y="nodeRadius + 16" fill="#2D3A7C"
                  font-size="11" font-weight="600"
                  style="pointer-events: none; user-select: none;">
              {{ truncar(no.nome, 14) }}
            </text>
            <text text-anchor="middle" :y="nodeRadius + 30" fill="#64748b"
                  font-size="9"
                  style="pointer-events: none; user-select: none;">
              {{ no.codigoIndicacao }}
            </text>
            <text text-anchor="middle" :y="nodeRadius + 42" fill="#94a3b8"
                  font-size="8" font-weight="600"
                  style="pointer-events: none; user-select: none;">
              ID #{{ no.membroId }}
            </text>
          </g>
        </g>
      </g>
    </svg>

    <div v-if="flatNodes.length === 0" class="vazio">
      Nenhum membro para exibir na árvore.
    </div>

    <div v-if="flatNodes.length > 0" class="legenda">
      <span class="legenda-item">
        <span class="dot dot-central"></span> Membro central
      </span>
      <span class="legenda-item">
        <span class="dot dot-filho"></span> Indicados
      </span>
      <span class="dica-uso">
        <i class="material-icons">pan_tool</i>
        Arraste · Scroll = zoom · Clique para ver detalhes
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, defineProps, defineEmits } from 'vue'

const props = defineProps({
  noCentral: { type: Object, required: true },
  downline: { type: Array, default: () => [] },
})
const emit = defineEmits(['no-clicado'])

const nodeRadius = 26
const hSpacing = 100
const vSpacing = 120

const svgEl = ref(null)
const tx = ref(0)
const ty = ref(0)
const scale = ref(1)
const arrastando = ref(false)
const arrasteInicial = ref({ x: 0, y: 0, tx: 0, ty: 0 })
const arrasteDistancia = ref(0)

const MIN_SCALE = 0.3
const MAX_SCALE = 3

const tree = computed(() => {
  const root = {
    membroId: props.noCentral.membroId,
    nome: props.noCentral.nomeMembro,
    codigoIndicacao: props.noCentral.codigoIndicacao,
    nivel: 0,
    children: [],
    isCentral: true,
  }
  const nivelCentral = props.noCentral.nivel ?? 0
  const mapa = new Map()
  mapa.set(root.membroId, root)

  const ordenada = [...props.downline].sort((a, b) => a.nivel - b.nivel)
  for (const n of ordenada) {
    const nodo = {
      membroId: n.membroId,
      nome: n.nomeMembro,
      codigoIndicacao: n.codigoIndicacao,
      nivel: n.nivel - nivelCentral,
      children: [],
      isCentral: false,
    }
    mapa.set(nodo.membroId, nodo)
    const pai = mapa.get(n.patrocinadorId)
    ;(pai || root).children.push(nodo)
  }
  return root
})

const edgesCache = ref([])

const flatNodes = computed(() => {
  const out = []
  const edges = []
  const root = tree.value
  if (!root.membroId) {
    edgesCache.value = []
    return []
  }

  function calcularLargura(no) {
    if (no.children.length === 0) { no._width = hSpacing; return hSpacing }
    let total = 0
    for (const c of no.children) total += calcularLargura(c)
    no._width = Math.max(total, hSpacing)
    return no._width
  }
  function posicionar(no, x, y) {
    no.x = x
    no.y = y
    out.push(no)
    if (no.children.length === 0) return
    let filhoX = x - no._width / 2
    for (const c of no.children) {
      filhoX += c._width / 2
      posicionar(c, filhoX, y + vSpacing)
      edges.push({ x1: x, y1: y + nodeRadius, x2: c.x, y2: y + vSpacing - nodeRadius })
      filhoX += c._width / 2
    }
  }

  calcularLargura(root)
  posicionar(root, Math.max(root._width / 2, 150), 60)
  edgesCache.value = edges
  return out
})

const edges = computed(() => {
  flatNodes.value
  return edgesCache.value
})

function iniciais(nome) {
  if (!nome) return '?'
  const partes = nome.trim().split(/\s+/)
  if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}
function truncar(texto, max) {
  if (!texto) return ''
  return texto.length > max ? texto.substring(0, max - 1) + '…' : texto
}

// ===== Pan =====
function iniciarArrasto(evt) {
  arrastando.value = true
  arrasteDistancia.value = 0
  arrasteInicial.value = { x: evt.clientX, y: evt.clientY, tx: tx.value, ty: ty.value }
}
function moverArrasto(evt) {
  if (!arrastando.value) return
  const dx = evt.clientX - arrasteInicial.value.x
  const dy = evt.clientY - arrasteInicial.value.y
  arrasteDistancia.value = Math.max(arrasteDistancia.value, Math.abs(dx) + Math.abs(dy))
  tx.value = arrasteInicial.value.tx + dx
  ty.value = arrasteInicial.value.ty + dy
}
function finalizarArrasto() {
  arrastando.value = false
}

// ===== Zoom =====
function aplicarZoom(novoScale, pivoX, pivoY) {
  const limitado = Math.max(MIN_SCALE, Math.min(MAX_SCALE, novoScale))
  if (!svgEl.value) { scale.value = limitado; return }
  const rect = svgEl.value.getBoundingClientRect()
  const px = pivoX - rect.left
  const py = pivoY - rect.top
  const svgX = (px - tx.value) / scale.value
  const svgY = (py - ty.value) / scale.value
  scale.value = limitado
  tx.value = px - svgX * limitado
  ty.value = py - svgY * limitado
}
function aoRodarMouse(evt) {
  const delta = -evt.deltaY * 0.001
  aplicarZoom(scale.value * (1 + delta), evt.clientX, evt.clientY)
}
function zoomIn() {
  if (!svgEl.value) return
  const r = svgEl.value.getBoundingClientRect()
  aplicarZoom(scale.value * 1.2, r.left + r.width / 2, r.top + r.height / 2)
}
function zoomOut() {
  if (!svgEl.value) return
  const r = svgEl.value.getBoundingClientRect()
  aplicarZoom(scale.value * 0.8, r.left + r.width / 2, r.top + r.height / 2)
}
function resetarView() {
  scale.value = 1
  centralizar()
}
function centralizar() {
  if (!svgEl.value || flatNodes.value.length === 0) return
  const rect = svgEl.value.getBoundingClientRect()
  const xs = flatNodes.value.map(n => n.x)
  const ys = flatNodes.value.map(n => n.y)
  const minX = Math.min(...xs) - 60
  const maxX = Math.max(...xs) + 60
  const minY = Math.min(...ys) - 60
  const maxY = Math.max(...ys) + 80
  const larg = maxX - minX
  const alt = maxY - minY
  const ex = rect.width / larg
  const ey = rect.height / alt
  scale.value = Math.min(ex, ey, 1)
  tx.value = (rect.width - larg * scale.value) / 2 - minX * scale.value
  ty.value = (rect.height - alt * scale.value) / 2 - minY * scale.value
}

// Clique só vale se o usuário não estiver arrastando
function clicarNo(evt, membroId) {
  if (arrasteDistancia.value > 4) return // movimento de arraste, não clique
  emit('no-clicado', membroId)
}

watch(() => [props.noCentral, props.downline], async () => {
  await nextTick()
  centralizar()
}, { deep: true })

onMounted(async () => {
  await nextTick()
  centralizar()
})
</script>

<style scoped>
.tree-container {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.controles {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  padding: 6px 10px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}
.btn-controle {
  width: 30px;
  height: 30px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #4f46e5;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s;
}
.btn-controle .material-icons { font-size: 18px; }
.btn-controle:hover {
  background: #4f46e5;
  color: #fff;
  border-color: #4f46e5;
}
.zoom-info {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 600;
  padding: 0 6px;
  min-width: 44px;
  text-align: center;
}

.arvore-svg {
  width: 100%;
  height: 520px;
  display: block;
  cursor: grab;
  user-select: none;
  touch-action: none;
}
.arvore-svg.cursor-grabbing {
  cursor: grabbing;
}

.tree-node {
  cursor: pointer;
}
.tree-node circle {
  transition: filter 0.15s, stroke-width 0.15s;
}
.tree-node:hover circle {
  stroke-width: 3.5;
  filter: drop-shadow(0 4px 10px rgba(79, 70, 229, 0.35));
}

.vazio {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-style: italic;
}

.legenda {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e2e8f0;
}
.legenda-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dica-uso {
  margin-left: auto;
  font-style: italic;
  font-size: 0.75rem;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dica-uso .material-icons { font-size: 14px; }
.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}
.dot-central {
  background: #4f46e5;
  border: 2px solid #fb6340;
}
.dot-filho {
  background: #fff;
  border: 2px solid #4f46e5;
}
</style>
